import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { List, X } from "@phosphor-icons/react";
import Logo from "../images/logo-mark.svg";
import { PROJECTS } from "../lib/projects";
import useSeo from "../lib/useSeo";
import ScrollToTop from "../lib/ScrollToTop";
import "./layout.scss";

// Labels are preserved verbatim from the previous navigation.
const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/hive", label: "HIVE" },
  { to: "/mk2", label: "MK2 Application" },
  { to: "/atmos", label: "ATMOS" },
  { to: "/vsc", label: "VSC" },
  { to: "/portablehood", label: "Portable Hood" },
  { to: "/contact", label: "Contact" },
];

// Derived from the project list so a new case study cannot be added on a dark
// ground by accident.
const CASE_ROUTES = new Set(PROJECTS.map((p) => p.slug));

const Layout = () => {
  const [isNavActive, setIsNavActive] = useState(false);
  const { pathname } = useLocation();

  useSeo();

  useEffect(() => {
    setIsNavActive(false);
  }, [pathname]);

  // Set on the root element rather than a wrapper so the fixed header and the
  // body background pick the palette up too.
  useEffect(() => {
    // A server that serves /hive as a directory hands back /hive/, which would
    // otherwise miss the lookup and leave the deck on the dark palette.
    const route = pathname.replace(/\/+$/, "") || "/";
    const light = CASE_ROUTES.has(route);
    document.documentElement.classList.toggle("theme-light", light);
    return () => document.documentElement.classList.remove("theme-light");
  }, [pathname]);

  // The overlay covers the page, so stop the document scrolling underneath
  // and let Escape close it.
  useEffect(() => {
    if (!isNavActive) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setIsNavActive(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [isNavActive]);

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="site-header">
        <Link to="/" className="site-logo" aria-label="Delaina Fernandes, home">
          <img src={Logo} alt="" width="83" height="54" />
        </Link>

        <nav className="site-nav" aria-label="Primary">
          <ul>
            {NAV.map(({ to, label, end }) => (
              <li key={to}>
                <NavLink to={to} end={end}>
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <button
          type="button"
          className="nav-toggle"
          onClick={() => setIsNavActive((v) => !v)}
          aria-expanded={isNavActive}
          aria-controls="overlay-menu"
          aria-label={isNavActive ? "Close menu" : "Open menu"}
        >
          {isNavActive ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
        </button>
      </header>

      <nav
        id="overlay-menu"
        aria-label="All pages"
        className={`custom-overlay-menu ${isNavActive ? "active" : "inactive"}`}
      >
        <ul>
          {NAV.map(({ to, label, end }, i) => (
            <li key={to} style={{ "--i": i }}>
              <NavLink to={to} end={end} onClick={() => setIsNavActive(false)}>
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <ScrollToTop />
      <main id="main">
        <Outlet />
      </main>
    </>
  );
};

export default Layout;
